
import { ref, Ref } from "vue";
import { Component, Vue } from "vue-property-decorator";

import { IRouteMeta } from "@/router/interface";
import program_routes from "@/api/routes/programs";
import structure_routes from "@/api/routes/structures";

import { ProgressLevel } from "@/models/program/version";
import StructureFactory from "@/models/structure/factory";

import { IProgramAssignment } from "../Assignments.vue";
import CProgramAssignmentsEditPositions from "./EditPositions.vue";
import CProgramAssignmentsEditWorkplaces from "./EditWorkplaces.vue";
import CProgramAssignmentsEditUsers from "./EditUsers.vue";
import CProgramAssignmentsEditProgram from "./EditProgram.vue";

export interface IChangingProgramAssignment {
  id: string | null;
  assignment_index: string | null;
}

export interface IChangedProgramAssignment {
  assignment: IProgramAssignment;
  assignment_index: string | null;
}

export interface IProgramAssignmentAvailableProgram {
  id: string;
  title: string;
  workplace_full_name: string | null;
}

export interface IProgramAssignmentEdit {
  id: string;
  level: number;
  program: IProgramAssignmentAvailableProgram;
  workplaces: string[]; // `${factory_id}_${infinity_id}`
  position_ids: string[];
  user_ids: string[];
}

@Component({
  name: "CProgramAssignmentsEdit",
  components: {
    CProgramAssignmentsEditPositions,
    CProgramAssignmentsEditWorkplaces,
    CProgramAssignmentsEditUsers,
    CProgramAssignmentsEditProgram
  }
})
export default class CProgramAssignmentsEdit extends Vue {
  private changing: IChangingProgramAssignment | null = null;

  private watchers: Function[] = [];

  public tabs = ["Настройки", "Должности", "Рабочие места", "Пользователи"];
  public current_tab: number = 0;

  public ProgressLevel = ProgressLevel;
  public level_key: number = 0;

  public selected_levels: Ref<Set<unknown>> = ref(new Set());
  public selected_program: IProgramAssignmentAvailableProgram | null = null;

  public selected_users: string[] = [];
  public selected_workplaces: string[] = [];
  public selected_positions: string[] = [];

  public factories: StructureFactory[] = [];

  protected beforeDestroy() {
    this.watchers.forEach(unwatch => {
      unwatch();
    });
  }

  protected async created() {
    this.watchers.push(
      this.$store.watch(
        state => state.changingProgramAssignment,
        async (
          changingProgramAssignment: IChangingProgramAssignment | null
        ) => {
          this.changing = changingProgramAssignment;

          if (this.changing) {
            this.setDefaultSettings();

            await this.loadAssignment();

            this.showModal();
          }
        }
      )
    );

    this.setDefaultSettings();

    await Promise.all([this.loadFactories()]);
  }

  public setDefaultSettings() {
    this.current_tab = 0;
    this.selected_program = null;
    this.selected_levels.value = new Set();

    this.selected_users = [];
    this.selected_workplaces = [];
    this.selected_positions = [];
  }

  public async loadAssignment() {
    if (!this.changing?.id) {
      return;
    }

    return this.$api
      .get(program_routes.assignment(this.changing.id))
      .then(({ data: res }: { data: IProgramAssignmentEdit }) => {
        this.selected_program = res.program;

        this.selected_positions = res.position_ids;
        this.selected_workplaces = res.workplaces;
        this.selected_users = res.user_ids;

        this.ProgressLevel.forEach(level => {
          if (level.id <= res.level) {
            this.selected_levels.value.add(level.id);
          }
        });
      });
  }

  private async loadFactories() {
    return this.$api
      .get(structure_routes.factories, {
        params: {
          only_my: true,
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: StructureFactory[] }) => {
        this.factories.push(...res);
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          speed: 500
        });
      });
  }

  public changeUsers(user_ids: string[]) {
    this.selected_users = user_ids;
  }

  public changePositions(position_ids: string[]) {
    this.selected_positions = position_ids;
  }

  public changeWorkplaces(workplace_ids: string[]) {
    this.selected_workplaces = workplace_ids;
  }

  public changeSelectedProgram(
    program: IProgramAssignmentAvailableProgram | null
  ) {
    this.selected_program = program;
  }

  public changeSelectedLevels(level_id: number) {
    if (this.selected_levels.value.has(level_id)) {
      this.ProgressLevel.forEach(level => {
        if (level.id >= level_id) {
          this.selected_levels.value.delete(level.id);
        }
      });
    } else {
      this.ProgressLevel.forEach(level => {
        if (level.id <= level_id) {
          this.selected_levels.value.add(level.id);
        }
      });
    }

    this.level_key++;
  }

  public async saveAssignment() {
    if (this.changing) {
      let req;

      const body = {
        program: this.selected_program,
        levels: Array.from(this.selected_levels.value),
        position_ids: this.selected_positions,
        workplaces: this.selected_workplaces,
        user_ids: this.selected_users
      };

      if (!body.program) {
        return this.$notify({
          group: "notifications",
          type: "error",
          text: "Выберите программу",
          speed: 500
        });
      }

      if (!body.levels.length) {
        return this.$notify({
          group: "notifications",
          type: "error",
          text: "Укажите уровни",
          speed: 500
        });
      }

      if (this.changing.id) {
        req = this.$api.put(program_routes.assignment(this.changing.id), body);
      } else {
        req = this.$api.post(program_routes.assignments, body);
      }

      req.then(({ data: res }: { data: IProgramAssignment }) => {
        this.$store.commit("changedProgramAssignment", {
          assignment: res,
          assignment_index: this.changing?.assignment_index ?? null
        });

        this.hideModal();
      });

      req.catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          speed: 500
        });
      });

      return req;
    }
  }

  public showModal() {
    this.$modal.show("change_program_assignment");
  }

  public hideModal() {
    this.$modal.hide("change_program_assignment");

    this.$store.commit("changingProgramAssignment", null);
  }

  public allowWrite() {
    return this.$api.allowWrite();
  }
}
